import { useState } from "react";
import { Modal, Button, Textarea, Group, Stack, Text } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconCalendarClock } from "@tabler/icons-react";
import { useCreateInvoiceFollowUpMutation } from "../api/queries/paymentsQueries";
import { toast } from "sonner";

interface FollowUpModalProps {
  externalId: string;
  opened: boolean;
  onClose: () => void;
}

export const FollowUpModal = ({
  externalId,
  opened,
  onClose,
}: FollowUpModalProps) => {
  const [followUpDate, setFollowUpDate] = useState<Date | null>(new Date());
  const [description, setDescription] = useState<string>("");
  const createFollowUp = useCreateInvoiceFollowUpMutation();

  const handleSave = async () => {
    if (!followUpDate) {
      toast.error("Please select a follow-up date");
      return;
    }

    // Validate that the date is not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(followUpDate);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      toast.error("Follow-up date cannot be in the past");
      return;
    }

    try {
      await createFollowUp.mutateAsync({
        externalId,
        follow_up_date: followUpDate.toISOString(),
        description: description.trim() || undefined,
      });
      toast.success("Follow-up scheduled successfully");
      onClose();
      setDescription("");
      setFollowUpDate(new Date());
    } catch (error) {
      console.error("Failed to create follow-up", error);
      toast.error("Failed to schedule follow-up");
    }
  };

  const handleClose = () => {
    onClose();
    setDescription("");
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      title={
        <Group gap="xs">
          <IconCalendarClock size={20} />
          <Text size="lg" fw={600}>
            Schedule Follow Up
          </Text>
          <Text size="sm" c="dimmed">
            After the follow-up date, the invoice will be marked as Unresolved.
            <br />
            Unless it is Approved.
          </Text>
        </Group>
      }
      radius="md"
      padding="xl"
      styles={{
        content: {
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      <Stack gap="md">
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 shadow-inner">
          <Group justify="space-between">
            <Text size="sm" fw={500} c="dimmed">
              Invoice
            </Text>
            <Text size="sm" fw={600}>
              {externalId}
            </Text>
          </Group>
        </div>

        <DateInput
          label="Follow-up date"
          placeholder="Pick date"
          value={followUpDate}
          onChange={setFollowUpDate}
          required
          radius="md"
          leftSection={<IconCalendarClock size={16} />}
          minDate={new Date()}
          styles={{
            label: {
              fontWeight: 500,
              marginBottom: 8,
            },
          }}
        />

        <Textarea
          label="Description (optional)"
          placeholder="Add details for this follow-up..."
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          minRows={4}
          radius="md"
          styles={{
            label: {
              fontWeight: 500,
              marginBottom: 8,
            },
          }}
        />

        <Group justify="flex-end" mt="lg">
          <Button
            variant="subtle"
            color="gray"
            radius="md"
            onClick={handleClose}
            disabled={createFollowUp.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={createFollowUp.isPending}
            color="green"
            radius="md"
            leftSection={<IconCalendarClock size={16} />}
          >
            Schedule Follow Up
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
